const pseudoUUID = () => {
  const getRand = () => {
    const rand = Math.floor(Math.random() * 1000000);
    const padded = String(rand).padStart(6, "0");
    return padded;
  };
  return `${getRand()}-${getRand()}-${getRand()}-${getRand()}-${getRand()}`;
};

type SubscriptionOptions = {
  receiveErrorUpdates?: boolean;
  receiveUserUpdates?: boolean;
  quiet?: boolean;
};

type Subscription = {
  id: string;
  topic: string;
  onEvent: (event: any) => void;
  options: SubscriptionOptions;
};

export type SubscribedWebsocket = {
  publish: (eventType: string, data?: any) => void;
  unsubscribe: () => void;
  topic: string;
  options: SubscriptionOptions;
};

type WebsocketAPIOptions = {
  websocketUrl: string;
  generateToken: () => Promise<string | null>;
};

export class WebsocketAPI {
  isConnected: boolean;
  isConnecting: boolean;
  subscriptions: Subscription[];
  queue: string[];
  failedConnections: number;
  socket: WebSocket | undefined;
  options: WebsocketAPIOptions;

  constructor(options: WebsocketAPIOptions) {
    this.isConnected = false;
    this.isConnecting = false;
    this.subscriptions = [];
    this.queue = [];
    this.failedConnections = 0;
    this.socket = undefined;

    this.options = options;
  }

  connect() {
    if (!this.options.websocketUrl || this.isConnecting || this.isConnected)
      return;

    this.isConnecting = true;
    this.options
      .generateToken()
      .then((token) => {
        if (!token) {
          this.isConnecting = false;
          return;
        }

        let connectionString = `${this.options.websocketUrl}?token=${token}`;
        if (this.failedConnections > 0) {
          connectionString += `&retry=${this.failedConnections}`;
        }
        this.socket = new WebSocket(connectionString);

        this.socket.onopen = () => {
          this.isConnecting = false;
          this.isConnected = true;
          this.failedConnections = 0;

          this.queue.forEach((q) => {
            this.sendOrQueue(q);
          });
          this.queue = [];
        };

        this.socket.onmessage = (event) => {
          if (event?.data) {
            const data = JSON.parse(event.data);
            this.subscriptions.forEach((s) => {
              if (s.topic === data.type && s.onEvent) {
                const { user, ...rest } = data.payload;
                if (user) {
                  // user.avatarUrl = util.avatarUrl(user);
                }
                s.onEvent({ user, ...rest });
              }
            });
          }
        };

        this.socket.onclose = (event) => {
          this.isConnecting = false;
          if (this.isConnected) {
            this.isConnected = false;

            // Try to reconnect immediately, after a few tries, poll every 30 seconds
            const maxInstantRetries = 4;
            const retryTimeout =
              this.failedConnections < maxInstantRetries ? 0 : 30000;

            const retry = () => {
              this.socket = undefined;
              this.failedConnections += 1;
              setTimeout(() => this.connect(), retryTimeout);

              if (this.failedConnections >= maxInstantRetries) {
                this.subscriptions.forEach((s) => {
                  if (s.options.receiveErrorUpdates) {
                    s.onEvent({ channel: "__websocketError" });
                  }
                });
              }
            };

            if (event.wasClean) {
              // If not regular shut-down due to logout, retry.
              if (event.code !== 1000) {
                retry();
              }
            } else {
              retry();
            }
          }
        };

        this.socket.onerror = () => {
          this.isConnecting = false;
          this.isConnected = false;
        };
      })
      .catch(() => {
        this.isConnecting = false;
        this.isConnected = false;
      });
  }

  disconnect() {
    this.isConnected = false;
    this.isConnecting = false;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      // If invoked manually we disconnect with a message to clear the user topics
      this.socket.send(JSON.stringify({ action: "disconnect" }));
      this.socket.close();
    }
  }

  subscribe(
    topic: string,
    onEvent: (event: any) => void,
    options: SubscriptionOptions = {
      receiveUserUpdates: false,
      receiveErrorUpdates: false,
      quiet: false,
    },
    onSubscribe = () => {},
  ): SubscribedWebsocket {
    const id = pseudoUUID();
    const subscription = { id, topic, onEvent, options };
    const { receiveUserUpdates = false, quiet = false } = options;

    this.subscriptions.push(subscription);

    // Slightly delay subscriptions to prevent issues when rapidly subscribing/unsubscribing
    setTimeout(() => {
      // Subscription may have been removed before this timer fired
      if (!this.subscriptions.some((s) => s.id === id)) {
        return;
      }
      this.sendMessage("subscribe", {
        topic,
        options: { receiveUserUpdates, quiet },
      });
      onSubscribe();
    }, 250);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const localThis = this;
    return {
      publish(eventType: string, data: any = {}) {
        if (!quiet) {
          localThis.sendMessage("publish", {
            topic,
            data: { channel: eventType, ...data },
          });
        }
      },
      unsubscribe() {
        const index = localThis.subscriptions.map((s) => s.id).indexOf(id);

        if (index > -1) {
          const sub = localThis.subscriptions[index];
          if (sub) {
            localThis.sendMessage("unsubscribe", { topic: sub.topic });
            localThis.subscriptions.splice(index, 1);
          }
        }
      },
      topic,
      options,
    };
  }

  unsubscribeAll() {
    this.subscriptions.forEach((s) => {
      this.sendMessage("unsubscribe", { topic: s.topic });
    });
    this.subscriptions = [];
  }

  sendMessage(action: string, data: any) {
    this.sendOrQueue(JSON.stringify({ action, ...data, _tstamp: Date.now() }));
  }

  sendOrQueue(message: string) {
    if (!this.isConnected) {
      // Only allow queueing of subscribe/unsubscribe messages, to prevent spamming the server is user disconnects and reconnects
      // Need to decode the message to check the action
      const { action } = JSON.parse(message);
      if (action === "subscribe" || action === "unsubscribe") {
        this.queue.push(message);
      }
    } else {
      this.socket?.send(message);
    }
  }
}
